import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Chip,
  Paper,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
  alpha
} from "@mui/material";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SavingsIcon from "@mui/icons-material/Savings";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HandshakeIcon from "@mui/icons-material/Handshake";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import StarsIcon from "@mui/icons-material/Stars";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import AssessmentIcon from "@mui/icons-material/Assessment";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";

import UserDataService from "./services/UserService";
import ReconciliationService from "./services/ReconciliationService";
import BusinessOverview from "./Business/BusinessOverview";
import LoanOverview from "./Loan/LoanOverview";
import { fNumber } from "../utils/formatNumber";
import "./dashboard.css";

export default function DashboardAppPage() {
  const auth = useSelector((state) => state.auth);
  const { user } = auth;
  const { user: currentUser } = useSelector((state) => state.auth);

  const [userData, setUserData] = useState(() => {
    try {
      const s = localStorage.getItem("user") || localStorage.getItem("profile");
      return s ? JSON.parse(s) : {};
    } catch {
      return {};
    }
  });
  const [dashboardTotals, setDashboardTotals] = useState(null);

  // Tab view for authorized officers: "cooperative": All Cooperative Accounts, "assigned": My Assigned Portfolio (if Fin II/III), "personal": My Personal Member Account
  const [activeTab, setActiveTab] = useState("personal");

  // Balance privacy state: hidden by default
  const [showBalances, setShowBalances] = useState(false);
  const [customCardVisibility, setCustomCardVisibility] = useState({});

  const isCardVisible = (key) =>
    customCardVisibility[key] !== undefined ? customCardVisibility[key] : showBalances;

  const toggleCardVisibility = (key) => {
    setCustomCardVisibility((prev) => ({
      ...prev,
      [key]: !(prev[key] !== undefined ? prev[key] : showBalances),
    }));
  };

  const toggleAllBalances = () => {
    setShowBalances((prev) => !prev);
    setCustomCardVisibility({});
  };

  // Fallback to stored profile and tolerant role matching
  const storedUser = useMemo(() => {
    if (currentUser) return currentUser;
    try {
      const s = localStorage.getItem("user") || localStorage.getItem("profile");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  }, [currentUser]);

  const [simRole, setSimRole] = useState(() => sessionStorage.getItem("mtc_view_as_role") || "");
  const [simUserId, setSimUserId] = useState(() => sessionStorage.getItem("mtc_view_as_user") || "");

  const normalizeRolesString = (u) => {
    if (!u) return "";
    const parts = [];
    if (Array.isArray(u.roles)) parts.push(...u.roles.map((r) => (typeof r === "object" ? r.name : String(r))));
    else if (u.roles) parts.push(String(u.roles));
    if (u.role) parts.push(Array.isArray(u.role) ? u.role.join(" ") : String(u.role));
    if (u.roleName) parts.push(String(u.roleName));
    if (u.isAdmin) parts.push("admin");
    if (u.isPresident) parts.push("president");
    return parts.join(" ").toLowerCase().replace(/role_/g, "");
  };

  const rolesString = useMemo(() => {
    if (simRole) return simRole.toLowerCase().replace(/role_/g, "");
    return normalizeRolesString(storedUser || user || currentUser);
  }, [simRole, storedUser, user, currentUser]);

  const isAdmin = rolesString.includes("admin");
  const isPresident = rolesString.includes("president");
  const isFinancial = rolesString.includes("financial");
  const isFinancialII = rolesString.includes("financial_2");
  const isFinancialIII = rolesString.includes("financial_3");
  const isFinancialI =
    (isFinancial && !isFinancialII && !isFinancialIII) ||
    rolesString.includes("financial_1");
  const isRestrictedFinancial = (isFinancialII || isFinancialIII) && !isFinancialI;
  const isRegistrar = rolesString.includes("registrar") || rolesString.includes("secretary");
  const isBusiness = rolesString.includes("business");

  // Only President, Financial I, and Admin can see All Cooperative accounts
  const canViewAllCooperative = (isAdmin || isPresident || isFinancialI) && !isRestrictedFinancial;
  const canViewAssignedPortfolio = isRestrictedFinancial || Boolean(dashboardTotals?.assignedPortfolio);
  const canViewBusinessOverview = isBusiness || isAdmin || isPresident;
  const canViewLoanOverview = isAdmin || isPresident || isFinancialI;
  const canAccessExecutiveTotals = canViewAllCooperative || canViewAssignedPortfolio;
  const showTabs = canViewAllCooperative || canViewAssignedPortfolio || canViewBusinessOverview || canViewLoanOverview;

  const targetUserId = simUserId || currentUser?.id || currentUser?._id || storedUser?.id || storedUser?._id;

  // Safeguard: Ensure role views are strictly enforced (Business on business, Fin II/III on assigned, Sec/Reg on personal)
  useEffect(() => {
    if (isBusiness) {
      if (activeTab !== "business" && activeTab !== "personal") {
        setActiveTab("business");
      }
    } else if (isRestrictedFinancial) {
      if (activeTab !== "personal" && activeTab !== "assigned") {
        setActiveTab("assigned");
      }
    } else if (canViewAllCooperative) {
      if (activeTab === "assigned") {
        setActiveTab("cooperative");
      } else if (activeTab === "personal" && !sessionStorage.getItem("mtc_user_selected_personal_tab")) {
        setActiveTab("cooperative");
      }
    } else {
      if (activeTab !== "personal") {
        setActiveTab("personal");
      }
    }
  }, [activeTab, canViewAllCooperative, isRestrictedFinancial, isBusiness, dashboardTotals]);

  // Listen for View As changes
  useEffect(() => {
    const handleViewAsChange = () => {
      setSimRole(sessionStorage.getItem("mtc_view_as_role") || "");
      setSimUserId(sessionStorage.getItem("mtc_view_as_user") || "");
    };
    window.addEventListener("mtc-view-as-change", handleViewAsChange);
    return () => window.removeEventListener("mtc-view-as-change", handleViewAsChange);
  }, []);

  // Fetch individual personal user data
  useEffect(() => {
    if (!targetUserId) return;
    UserDataService.get(targetUserId)
      .then((response) => {
        setUserData(response.data || {});
      })
      .catch((e) => {
        console.error("Dashboard UserDataService.get error:", e);
      });
  }, [targetUserId, simUserId]);

  // Fetch executive dashboard totals (President, Financial I, II, III, Admin)
  const fetchDashboardTotals = useCallback(async () => {
    if (!canAccessExecutiveTotals) {
      return;
    }
    try {
      const res = await ReconciliationService.getTotals();
      setDashboardTotals(res.data || null);
    } catch (err) {
      console.error("Failed to load dashboard totals:", err);
    }
  }, [canAccessExecutiveTotals]);

  useEffect(() => {
    fetchDashboardTotals();
  }, [fetchDashboardTotals, simRole, simUserId]);


  // Definition of Primary Accounts
  const primaryAccounts = [
    { key: "shares", userKey: "sharesBalance", label: "Total Shares", color: "#0B3B60", icon: <TrendingUpIcon fontSize="small" /> },
    { key: "savings", userKey: "savingsBalance", label: "Total Savings", color: "#059669", icon: <SavingsIcon fontSize="small" /> },
    { key: "loan", userKey: "loanBalance", label: "Total Loan", color: "#D97706", icon: <CreditScoreIcon fontSize="small" /> },
    { key: "dev", userKey: "devBalance", label: "Total Devt. Levy", color: "#2563EB", icon: <AccountBalanceWalletIcon fontSize="small" /> },
  ];

  // Definition of Auxiliary Accounts (Business Disbursed visible on all user dashboards)
  const auxiliaryAccounts = [
    { key: "wel", userKey: "welBalance", label: "Total Welfare", color: "#7C3AED", icon: <HandshakeIcon fontSize="small" /> },
    { key: "takaful", userKey: "takafulBalance", label: "Total Takaful", color: "#0E7490", icon: <HealthAndSafetyIcon fontSize="small" /> },
    { key: "min", userKey: "minBalance", label: "Total Minutes", color: "#0284C7", icon: <AssignmentIcon fontSize="small" /> },
    { key: "ileya", userKey: "ileyaSavingsBalance", label: "Total Ileya Savings", color: "#EA580C", icon: <EventAvailableIcon fontSize="small" /> },
    { key: "speacc", userKey: "speAccBalance", label: "Total Special Account", color: "#4F46E5", icon: <StarsIcon fontSize="small" /> },
    {
      key: "business",
      userKey: "businessBalance",
      label: (canViewAllCooperative && activeTab === "cooperative") ? "Total Business Disbursed" : "Business Disbursed",
      color: "#0D9488",
      icon: <StorefrontIcon fontSize="small" />
    }
  ];

  // Helper to determine which amount to display based on active tab and officer status
  const getDisplayAmount = (accountItem) => {
    if (activeTab === "personal" || !canAccessExecutiveTotals) {
      // Personal Member Balances
      return Number(userData?.[accountItem.userKey]) || 0;
    }

    if (activeTab === "assigned" && dashboardTotals?.assignedPortfolio) {
      // Assigned Portfolio Totals (for Financial II / III)
      return Number(dashboardTotals.assignedPortfolio[accountItem.key]) || 0;
    }

    // Cooperative Account Totals (All Members + Net Reconciliations) - only for President, Financial I, Admin
    if (canViewAllCooperative && activeTab === "cooperative" && dashboardTotals?.accountTotals) {
      if (accountItem.key === "business") {
        return Number(dashboardTotals.totalDisbursedToBusiness ?? dashboardTotals.accountTotals.totalDisbursedToBusiness ?? dashboardTotals.accountTotals.business) || 0;
      }
      return Number(dashboardTotals.accountTotals[accountItem.key]) || 0;
    }

    // Fallback to personal if totals not yet loaded
    return Number(userData?.[accountItem.userKey]) || 0;
  };

  // Helper for grand total (Loan is subtracted because it represents borrowed funds / disbursement liability)
  const grandTotal = useMemo(() => {
    if (activeTab === "personal" || !canAccessExecutiveTotals) {
      const shares = Number(userData.sharesBalance) || 0;
      const savings = Number(userData.savingsBalance) || 0;
      const loan = Number(userData.loanBalance) || 0;
      const ileya = Number(userData.ileyaSavingsBalance) || 0;
      const speacc = Number(userData.speAccBalance) || 0;
      // Only shares, savings, ileya, and special account sum up (dev, wel, and min are for running the association)
      return (shares + savings + ileya + speacc) - loan;
    }

    if (activeTab === "assigned" && dashboardTotals?.assignedPortfolio) {
      return Number(dashboardTotals.assignedPortfolio.grandTotal) || 0;
    }

    if (canViewAllCooperative && activeTab === "cooperative" && dashboardTotals?.accountTotals) {
      return Number(dashboardTotals.accountTotals.grandTotal) || 0;
    }

    return 0;
  }, [canViewAllCooperative, canAccessExecutiveTotals, activeTab, userData, dashboardTotals]);

  // Role Badge Label
  const roleTitle = useMemo(() => {
    if (isAdmin) return "Overall Administrator Dashboard";
    if (isPresident) return "President Executive Dashboard";
    if (isFinancialI) return "Financial I Executive Dashboard";
    if (isFinancialII) return "Financial II Portfolio Dashboard";
    if (isFinancialIII) return "Financial III Portfolio Dashboard";
    if (isBusiness) return "Business Officer Portfolio";
    if (isRegistrar) return "Secretary / Registrar Dashboard";
    return "Member Financial Portfolio";
  }, [isAdmin, isPresident, isFinancialI, isFinancialII, isFinancialIII, isBusiness, isRegistrar]);

  const renderCard = (b) => {
    const amount = getDisplayAmount(b);
    const reconNet = dashboardTotals?.reconciliationTotals?.[b.key] || 0;
    const baseMemberAmt = dashboardTotals?.baseCooperativeTotals?.[b.key] || 0;

    let subLabel = "Active cooperative ledger";
    if (activeTab === "personal" || !canAccessExecutiveTotals) {
      subLabel = "Personal member ledger";
    } else if (activeTab === "assigned") {
      subLabel = "Assigned member portfolio";
    } else if (reconNet !== 0) {
      subLabel = `Base: ₦${fNumber(baseMemberAmt)} • Adj: ${reconNet > 0 ? "+" : ""}₦${fNumber(reconNet)}`;
    }

    return (
      <Box
        sx={{
          backgroundColor: "#ffffff",
          borderRadius: "14px",
          border: "1px solid #E2E8F0",
          p: { xs: 2, sm: 2.25, md: 2.5 },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px 0 rgba(0, 0, 0, 0.03)",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: "0 10px 20px -3px rgba(11, 59, 96, 0.08), 0 4px 6px -2px rgba(11, 59, 96, 0.04)",
            borderColor: "#CBD5E1",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "4px",
            backgroundColor: b.color || "#0B3B60",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "#64748B",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontSize: { xs: "0.75rem", sm: "0.8rem" },
            }}
          >
            {activeTab === "personal" || !canAccessExecutiveTotals ? b.label.replace("Total ", "") : b.label}
          </Typography>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              backgroundColor: alpha(b.color || "#0B3B60", 0.09),
              color: b.color || "#0B3B60",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              ml: 1,
            }}
          >
            {b.icon}
          </Box>
        </Box>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 0.5 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.35rem", sm: "1.5rem", md: "1.75rem" },
                color: "#0B3B60",
                fontFamily: "'Inter', sans-serif",
                letterSpacing: "-0.02em",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                lineHeight: 1.2,
              }}
            >
              {isCardVisible(b.key) ? `₦${fNumber(amount)}` : "₦••••••"}
            </Typography>
            <Tooltip title={isCardVisible(b.key) ? `Hide ${b.label}` : `Show ${b.label}`}>
              <IconButton
                size="small"
                onClick={() => toggleCardVisibility(b.key)}
                sx={{
                  color: "#94A3B8",
                  p: 0.5,
                  "&:hover": { color: "#0B3B60", backgroundColor: "rgba(11, 59, 96, 0.06)" },
                }}
              >
                {isCardVisible(b.key) ? <VisibilityOffIcon sx={{ fontSize: "1.1rem" }} /> : <VisibilityIcon sx={{ fontSize: "1.1rem" }} />}
              </IconButton>
            </Tooltip>
          </Box>

          <Typography variant="caption" sx={{ color: "#94A3B8", display: "block", mt: 0.5, fontSize: "0.72rem" }}>
            {subLabel}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
      {/* Executive Welcome Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 0.5 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: "#0F172A",
                letterSpacing: "-0.02em",
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" },
              }}
            >
              Welcome back, {userData.firstName || currentUser?.firstName || (currentUser?.name ? currentUser.name.split(' ')[0] : '') || "Member"}
            </Typography>

            <Chip
              label={roleTitle}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: "0.75rem",
                backgroundColor: isAdmin ? "#DC2626" : isPresident ? "#0B3B60" : isFinancialI ? "#059669" : isRestrictedFinancial ? "#D97706" : "#0B3B60",
                color: "#FFFFFF",
              }}
            />
          </Box>

          <Typography variant="body2" sx={{ color: "#64748B", fontSize: { xs: "0.85rem", sm: "0.925rem" } }}>
            {canViewAllCooperative
              ? "Comprehensive real-time overview of all cooperative accounts and financial balances."
              : isRestrictedFinancial
              ? "Portfolio overview of members allocated under your financial supervision."
              : "Mutual Trust Cooperative real-time personal member financial portfolio overview."}
          </Typography>
        </Box>

        {/* Action Buttons for Officers & Balances Privacy Toggle */}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", width: { xs: "100%", sm: "auto" } }}>
          {/* Global Show / Hide Balances Toggle (Hidden by default) */}
          <Button
            variant={showBalances ? "outlined" : "contained"}
            startIcon={showBalances ? <VisibilityOffIcon /> : <VisibilityIcon />}
            onClick={toggleAllBalances}
            sx={{
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
              px: 2,
              py: 1,
              fontSize: "0.85rem",
              backgroundColor: showBalances ? "transparent" : "#0B3B60",
              borderColor: "#CBD5E1",
              color: showBalances ? "#0B3B60" : "#FFFFFF",
              "&:hover": {
                backgroundColor: showBalances ? "#F1F5F9" : "#072740",
                borderColor: "#0B3B60",
              },
            }}
          >
            {showBalances ? "Hide Balances" : "Show Balances"}
          </Button>

          {/* Account Reconciliation button for Financial I and Admin */}
          {(isFinancialI || isAdmin) && (
            <Button
              component={Link}
              to="/dashboard/reconciliation"
              variant="contained"
              startIcon={<FactCheckIcon />}
              sx={{
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "8px",
                px: 2.2,
                py: 1,
                backgroundColor: "#059669",
                "&:hover": { backgroundColor: "#047857" },
                boxShadow: "0 4px 14px rgba(5, 150, 105, 0.25)",
              }}
            >
              Account Reconciliation
            </Button>
          )}

          {/* Summary Account link for authorized financial officers */}
          {canAccessExecutiveTotals && (
            <Button
              component={Link}
              to="/dashboard/summary"
              variant="outlined"
              startIcon={<AssessmentIcon />}
              sx={{
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "8px",
                px: 2,
                py: 1,
                borderColor: "#CBD5E1",
                color: "#0B3B60",
                "&:hover": { borderColor: "#0B3B60", backgroundColor: "#F8FAFC" }
              }}
            >
              Summary Statement
            </Button>
          )}

          {/* Refresh button for executive accounts */}
          {canAccessExecutiveTotals && (
            <Tooltip title="Refresh account balances">
              <IconButton onClick={fetchDashboardTotals} sx={{ border: "1px solid #CBD5E1", borderRadius: 2 }}>
                <RefreshIcon sx={{ color: "#0B3B60" }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Register Member link (admin and registrar only, not president) */}
          {(isAdmin || isRegistrar) && !isPresident && (
            <Button
              component={Link}
              to="/signup"
              variant="contained"
              color="primary"
              startIcon={<PersonAddAltIcon />}
              sx={{
                fontWeight: 700,
                textTransform: "none",
                borderRadius: "8px",
                px: 2.2,
                py: 1,
                boxShadow: "0 4px 14px rgba(11, 59, 96, 0.25)",
              }}
            >
              Register Member
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabs: Switch between views (All Cooperative Accounts is exclusively for President, Fin I, Admin) */}
      {showTabs && (
        <Paper sx={{ mb: 3, borderRadius: 2.5, border: "1px solid #E2E8F0", overflow: "hidden", backgroundColor: "#FFFFFF" }}>
          <Tabs
            value={activeTab}
            onChange={(e, newTab) => {
              sessionStorage.setItem("mtc_user_selected_personal_tab", newTab === "personal" ? "1" : "");
              setActiveTab(newTab);
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTab-root": {
                fontWeight: 700,
                textTransform: "none",
                minHeight: 48,
                fontSize: "0.875rem",
              },
              "& .Mui-selected": {
                color: "#0B3B60",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#0B3B60",
                height: 3,
              },
            }}
          >
            {canViewAllCooperative && (
              <Tab
                value="cooperative"
                icon={<AccountBalanceIcon sx={{ fontSize: "1.1rem" }} />}
                iconPosition="start"
                label={`All Cooperative Accounts (${dashboardTotals?.totalMembers || 0} Members)`}
              />
            )}

            {canViewBusinessOverview && (
              <Tab
                value="business"
                icon={<StorefrontIcon sx={{ fontSize: "1.1rem" }} />}
                iconPosition="start"
                label="Business Portfolio & Analytics"
              />
            )}

            {canViewLoanOverview && (
              <Tab
                value="loan"
                icon={<CreditScoreIcon sx={{ fontSize: "1.1rem" }} />}
                iconPosition="start"
                label="Loan Portfolio & Analytics"
              />
            )}

            {canViewAssignedPortfolio && (
              <Tab
                value="assigned"
                icon={<GroupIcon sx={{ fontSize: "1.1rem" }} />}
                iconPosition="start"
                label={`My Assigned Portfolio (${dashboardTotals?.assignedPortfolio?.assignedCount || 0} Members)`}
              />
            )}

            <Tab
              value="personal"
              icon={<PersonIcon sx={{ fontSize: "1.1rem" }} />}
              iconPosition="start"
              label="My Personal Member Account"
            />
          </Tabs>
        </Paper>
      )}

      {activeTab === "business" ? (
        <BusinessOverview />
      ) : activeTab === "loan" ? (
        <LoanOverview />
      ) : (
        <>

      {/* Grand Total Cooperative / Personal Portfolio Banner */}
      <Paper
        sx={{
          mb: 4,
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          background: "linear-gradient(135deg, #0B3B60 0%, #072740 100%)",
          color: "#FFFFFF",
          boxShadow: "0 12px 32px -8px rgba(11, 59, 96, 0.35)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={8}>
              <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.75)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {activeTab === "personal" || !canAccessExecutiveTotals
                  ? "Your Total Personal Contribution Balance (Net of Loans)"
                  : activeTab === "assigned"
                  ? "Total Allocated Portfolio Under Your Management (Net of Loans)"
                  : "Overall Cooperative Combined Financial Portfolio (Net of Loans)"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5, mb: 1, flexWrap: "wrap" }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                    letterSpacing: "-0.02em",
                    color: "#FFFFFF",
                    lineHeight: 1.15,
                  }}
                >
                  {isCardVisible("grandTotal")
                    ? (grandTotal < 0 ? `-₦${fNumber(Math.abs(grandTotal))}` : `₦${fNumber(grandTotal)}`)
                    : "₦••••••••"}
                </Typography>
                <Tooltip title={isCardVisible("grandTotal") ? "Hide Grand Total" : "Show Grand Total"}>
                  <IconButton
                    onClick={() => toggleCardVisibility("grandTotal")}
                    size="small"
                    sx={{
                      color: "rgba(255, 255, 255, 0.75)",
                      backgroundColor: "rgba(255, 255, 255, 0.12)",
                      "&:hover": { color: "#FFFFFF", backgroundColor: "rgba(255, 255, 255, 0.22)" },
                    }}
                  >
                    {isCardVisible("grandTotal") ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.85)" }}>
                {activeTab === "personal" || !canAccessExecutiveTotals
                  ? "Sum of your personal member balances (Shares, Savings, Ileya, and Special Account) minus any outstanding loan balance."
                  : activeTab === "assigned"
                  ? `Combined net portfolio of all ${dashboardTotals?.assignedPortfolio?.assignedCount || 0} members officially allocated to you (Total contributions minus Loans).`
                  : `Aggregated cooperative net sum across standard cooperative accounts (Shares + Savings + Ileya + Dev + Min + Wel + Takaful + SpeAcc minus Loans)${dashboardTotals?.reconciliationTotals?.netTotal ? ` including ₦${fNumber(dashboardTotals.reconciliationTotals.netTotal)} net reconciliations` : ""}.`}
              </Typography>
            </Grid>

            {/* Quick Action Button inside Banner */}
            {(isFinancialI || isAdmin) && (
              <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
                <Button
                  component={Link}
                  to="/dashboard/reconciliation"
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    backgroundColor: "#059669",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    textTransform: "none",
                    px: 3,
                    py: 1.2,
                    borderRadius: 2,
                    boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
                    "&:hover": { backgroundColor: "#047857" },
                  }}
                >
                  Reconcile Accounts
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
      </Paper>

      {/* Primary Financial Accounts (Top Row) */}
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 800,
            color: "#0B3B60",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontSize: "0.78rem",
            mb: 1.5,
          }}
        >
          Primary Financial Accounts
        </Typography>
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
          {primaryAccounts.map((b) => (
            <Grid item xs={12} sm={6} md={6} lg={3} key={b.key}>
              {renderCard(b)}
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Auxiliary Cooperative Accounts (Secondary Row) */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 800,
            color: "#0B3B60",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontSize: "0.78rem",
            mb: 1.5,
          }}
        >
          Auxiliary Accounts & Ledgers
        </Typography>
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
          {auxiliaryAccounts.map((b) => (
            <Grid item xs={12} sm={6} md={6} lg={3} key={b.key}>
              {renderCard(b)}
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Financial I Quick Reconciliation Callout Card */}
      {(isFinancialI || isAdmin) && (
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            border: "1px solid #E2E8F0",
            backgroundColor: "#FFFFFF",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "12px",
                  backgroundColor: alpha("#059669", 0.1),
                  color: "#059669",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FactCheckIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0B3B60" }}>
                  Financial I Account Reconciliation Management
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748B" }}>
                  Record direct deductions or credits (e.g. deduct ₦50,000 for Business capitalisation or ₦10,000 for secretariat stationaries) with date of transaction and timestamp.
                </Typography>
              </Box>
            </Box>

            <Button
              component={Link}
              to="/dashboard/reconciliation"
              variant="contained"
              sx={{
                backgroundColor: "#0B3B60",
                "&:hover": { backgroundColor: "#072740" },
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                px: 3,
                py: 1,
              }}
            >
              Open Reconciliation Ledger
            </Button>
          </Box>
        </Paper>
      )}
        </>
      )}
    </Container>
  );
}
